import { EComboItemType } from "../../Enum/EComboItemType"
import { AddtionalEntity } from "../../Objects/Addtional/AddtionalEntity"
import { AwardEntity } from "../../Objects/Awards/AwardEntity"
import { CategoryEntity } from "../../Objects/Category/CategoryEntity"
import { ComboEntity } from "../../Objects/Combo/ComboEntity"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { CatalogItemEntity } from "../../Objects/ProductsAndCatalogItems/CatalogItemEntity"
import { ProductEntity } from "../../Objects/ProductsAndCatalogItems/ProductEntity"
import { Promotions } from "../../Objects/Promotion/Promotions"
import { SelectionEntity } from "../../Objects/Selection/SelectionEntity"
import { AssociationItem } from "./AssociationsPopup"

const getCatalogProductTitle = (name: string): string => `${name}`

export const resolvePromotionAssociations = (
  promotion: Promotions,
  products: ProductEntity[],
  catalogItems: CatalogItemEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const items: AssociationItem[] = []

  products.forEach((product) => {
    if (product.catalogProductEntity.promotionsIds.includes(promotion.id)) {
      items.push({ id: product.id, title: getCatalogProductTitle(product.catalogProductEntity.name), badge: "Produto" })
    }
  })

  catalogItems.forEach((catalogItem) => {
    if (catalogItem.catalogProductEntity.promotionsIds.includes(promotion.id)) {
      items.push({ id: catalogItem.id, title: getCatalogProductTitle(catalogItem.catalogProductEntity.name), badge: "Item complementar" })
    }
  })

  combos.forEach((combo) => {
    if (combo.catalogProductEntity.promotionsIds.includes(promotion.id)) {
      items.push({ id: combo.id, title: getCatalogProductTitle(combo.catalogProductEntity.name), badge: "Combo" })
    }
  })

  return items
}

export const resolveAwardAssociations = (award: AwardEntity, customers: CustomerEntity[]): AssociationItem[] => {
  return customers
    .filter(
      (customer) =>
        customer.awardsIds.includes(award.id) || customer.awardManagers.some((manager) => manager.awardId === award.id),
    )
    .map((customer) => ({
      id: customer.id,
      title: customer.personEntity.name,
      subtitle: `CPF: ${customer.personEntity.cpf || "-"}`,
      badge: "Cliente",
    }))
}

export const resolveCategoryAssociations = (
  category: CategoryEntity,
  products: ProductEntity[],
  catalogItems: CatalogItemEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const items: AssociationItem[] = []

  products.forEach((product) => {
    if (product.catalogProductEntity.categoryId === category.id) {
      items.push({ id: product.id, title: product.catalogProductEntity.name, badge: "Produto" })
    }
  })

  catalogItems.forEach((catalogItem) => {
    if (catalogItem.catalogProductEntity.categoryId === category.id) {
      items.push({ id: catalogItem.id, title: catalogItem.catalogProductEntity.name, badge: "Item complementar" })
    }
  })

  combos.forEach((combo) => {
    if (combo.catalogProductEntity.categoryId === category.id) {
      items.push({ id: combo.id, title: combo.catalogProductEntity.name, badge: "Combo" })
    }
  })

  return items
}

export const resolveSelectionAssociations = (
  selection: SelectionEntity,
  products: ProductEntity[],
  catalogItems: CatalogItemEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const hasSelection = (ids: number[]) => ids.includes(selection.id)

  const items: AssociationItem[] = []

  products.forEach((product) => {
    if (hasSelection(product.catalogProductEntity.allowedSelections.map((e) => e.id))) {
      items.push({ id: product.id, title: product.catalogProductEntity.name, badge: "Produto" })
    }
  })

  catalogItems.forEach((catalogItem) => {
    if (hasSelection(catalogItem.catalogProductEntity.allowedSelections.map((e) => e.id))) {
      items.push({ id: catalogItem.id, title: catalogItem.catalogProductEntity.name, badge: "Item complementar" })
    }
  })

  combos.forEach((combo) => {
    if (hasSelection(combo.catalogProductEntity.allowedSelections.map((e) => e.id))) {
      items.push({ id: combo.id, title: combo.catalogProductEntity.name, badge: "Combo" })
    }
  })

  return items
}

export const resolveAdditionalAssociations = (
  additional: AddtionalEntity,
  products: ProductEntity[],
  catalogItems: CatalogItemEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const hasAdditional = (ids: number[]) => ids.includes(additional.id)

  const items: AssociationItem[] = []

  products.forEach((product) => {
    const ids = [
      ...product.catalogProductEntity.allowedAddtionals.map((e) => e.id),
      ...product.catalogProductEntity.freeAllowedAddtionals.map((e) => e.id),
    ]
    if (hasAdditional(ids)) {
      items.push({ id: product.id, title: product.catalogProductEntity.name, badge: "Produto" })
    }
  })

  catalogItems.forEach((catalogItem) => {
    const ids = [
      ...catalogItem.catalogProductEntity.allowedAddtionals.map((e) => e.id),
      ...catalogItem.catalogProductEntity.freeAllowedAddtionals.map((e) => e.id),
    ]
    if (hasAdditional(ids)) {
      items.push({ id: catalogItem.id, title: catalogItem.catalogProductEntity.name, badge: "Item complementar" })
    }
  })

  combos.forEach((combo) => {
    const ids = [
      ...combo.catalogProductEntity.allowedAddtionals.map((e) => e.id),
      ...combo.catalogProductEntity.freeAllowedAddtionals.map((e) => e.id),
    ]
    if (hasAdditional(ids)) {
      items.push({ id: combo.id, title: combo.catalogProductEntity.name, badge: "Combo" })
    }
  })

  return items
}

export const resolveCatalogItemComboAssociations = (catalogItem: CatalogItemEntity, combos: ComboEntity[]): AssociationItem[] => {
  return combos
    .filter((combo) => combo.items.some((item) => item.itemType === EComboItemType.CatalogItem && item.itemId === catalogItem.id))
    .map((combo) => ({ id: combo.id, title: combo.catalogProductEntity.name, badge: "Combo" }))
}

export const resolveProductComboAssociations = (product: ProductEntity, combos: ComboEntity[]): AssociationItem[] => {
  return combos
    .filter((combo) => combo.items.some((item) => item.itemType === EComboItemType.Product && item.itemId === product.id))
    .map((combo) => ({ id: combo.id, title: combo.catalogProductEntity.name, badge: "Combo" }))
}
